import React, { useMemo } from 'react';
import { Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import { Est3WbsLayerTag } from './Est3WbsLayerTag';

import type { Est3WbsNode } from '@/utils/est3WbsTree';

export type Est3WbsTreeRow = Est3WbsNode & {
  key?: string;
  layer_code?: string;
  level_hint?: number;
  short_name?: string;
  reference_scope_code?: string;
  description?: string;
  children?: Est3WbsTreeRow[];
};

export interface Est3WbsTreeTableProps {
  dataSource: Est3WbsTreeRow[];
  loading?: boolean;
  selectedId?: string;
  onSelect?: (row: Est3WbsTreeRow) => void;
  readOnly?: boolean;
  expandedRowKeys?: React.Key[];
  onExpandedRowsChange?: (keys: React.Key[]) => void;
  extraColumns?: TableProps<Est3WbsTreeRow>['columns'];
  size?: 'small' | 'middle';
  /** Library editor: wider name, no depth column */
  variant?: 'default' | 'library';
  searchFilter?: string;
}

export const Est3WbsTreeTable: React.FC<Est3WbsTreeTableProps> = ({
  dataSource,
  loading,
  selectedId,
  onSelect,
  readOnly,
  expandedRowKeys,
  onExpandedRowsChange,
  extraColumns,
  size = 'small',
  variant = 'default',
  searchFilter,
}) => {
  const filteredData = useMemo(() => {
    const q = searchFilter?.trim().toLowerCase();
    if (!q) return dataSource;
    const filterTree = (rows: Est3WbsTreeRow[]): Est3WbsTreeRow[] =>
      rows
        .map((row) => {
          const kids = row.children?.length ? filterTree(row.children) : [];
          const selfMatch =
            row.code.toLowerCase().includes(q) ||
            (row.name || '').toLowerCase().includes(q) ||
            (row.short_name || '').toLowerCase().includes(q);
          if (selfMatch || kids.length) return { ...row, children: kids.length ? kids : undefined };
          return null;
        })
        .filter(Boolean) as Est3WbsTreeRow[];
    return filterTree(dataSource);
  }, [dataSource, searchFilter]);

  const baseColumns: TableProps<Est3WbsTreeRow>['columns'] =
    variant === 'library'
      ? [
          { title: 'Code', dataIndex: 'code', width: 88, fixed: 'left' as const },
          {
            title: 'Name',
            dataIndex: 'name',
            ellipsis: true,
            render: (name: string, row) => (
              <Typography.Text
                strong={row.layer_code === 'PACKAGE'}
                ellipsis={{ tooltip: name }}
              >
                {name}
              </Typography.Text>
            ),
          },
          {
            title: 'Layer',
            width: 92,
            render: (_: unknown, row) => <Est3WbsLayerTag layerCode={row.layer_code} showHint={false} />,
          },
        ]
      : [
          { title: 'Code', dataIndex: 'code', width: 110 },
          { title: 'Name', dataIndex: 'name', ellipsis: true },
          {
            title: 'Layer',
            width: 100,
            render: (_: unknown, row) => <Est3WbsLayerTag layerCode={row.layer_code} />,
          },
          {
            title: 'Depth',
            dataIndex: 'level_hint',
            width: 64,
            render: (v) => (v != null ? v : '—'),
          },
        ];

  return (
    <Table<Est3WbsTreeRow>
      size={size}
      rowKey="id"
      loading={loading}
      dataSource={filteredData}
      pagination={false}
      scroll={variant === 'library' ? { x: 420 } : undefined}
      expandable={{
        defaultExpandAllRows: expandedRowKeys === undefined,
        expandedRowKeys,
        onExpandedRowsChange: onExpandedRowsChange
          ? (keys) => onExpandedRowsChange(keys as React.Key[])
          : undefined,
      }}
      onRow={(row) => ({
        onClick: () => onSelect?.(row),
        style: {
          cursor: onSelect ? 'pointer' : undefined,
          background:
            selectedId === String(row.id)
              ? '#e6f4ff'
              : row.layer_code === 'PACKAGE'
                ? 'rgba(114, 46, 209, 0.04)'
                : undefined,
        },
      })}
      columns={[...baseColumns, ...(extraColumns || [])]}
    />
  );
};
